import { Module } from 'vuex';
import { api } from '../../plugins/salut-api';
import { NgoSignupState, RootState } from '../types';
import { NgoSignupBasicDto, NgoSignupExtraDto } from '../../types/ngo-signup';
import router from '../../router';
import { ProfileActionKeys } from './profile';
import { AuthActionKeys } from './auth';

const SET_BASIC_DATA = 'SET_BASIC_DATA';
const SET_EXTRA_DATA = 'SET_EXTRA_DATA';
const SET_CURRENT_STEP = 'SET_CURRENT_STEP';

export const NgoSignupActionKeys = {
	setBasicData: 'setBasicData',
	setExtraData: 'setExtraData',
	submitBasic: 'submitBasic',
	requestEmailVerification: 'requestEmailVerification',
	submitExtra: 'submitExtra',
	setCurrentStep: 'setCurrentStep',
	changeAccountEmail: 'changeAccountEmail',
};

export const NgoSignupGetterKeys = {
	basicData: 'basicData',
	extraData: 'extraData',
	currentStep: 'currentStep',
};

const initialState: NgoSignupState = {
	basicData: {},
	extraData: {},
	currentStep: 1,
};

const store: Module<NgoSignupState, RootState> = {
	namespaced: true,

	state: { ...initialState },

	mutations: {
		[SET_BASIC_DATA](state, data: Partial<NgoSignupBasicDto>) {
			state.basicData = { ...state.basicData, ...data };
		},
		[SET_EXTRA_DATA](state, data: Partial<NgoSignupExtraDto>) {
			state.extraData = { ...state.extraData, ...data };
		},
		[SET_CURRENT_STEP](state, step: number) {
			state.currentStep = step;
		},
	},

	actions: {
		[NgoSignupActionKeys.setBasicData]({ commit }, { basicData }) {
			commit(SET_BASIC_DATA, basicData);
		},

		[NgoSignupActionKeys.setExtraData]({ commit }, { extraData }) {
			commit(SET_EXTRA_DATA, extraData);
		},

		async [NgoSignupActionKeys.submitBasic]({ state, rootGetters, dispatch }) {
			// state.data.code = 'X' + Math.round(Math.random() * 10000);
			const ngo = await api.ngoSignupBasic(
				state.basicData as NgoSignupBasicDto,
			);
			if (!ngo) throw new Error('Unexpected error creating NGO');
			const lang = rootGetters['app/lang'];
			dispatch('app/setPreferenceLang', { lang: lang }, { root: true });
			// login first
			await dispatch(
				'auth/login',
				{
					email: state.basicData.accountEmail,
					password: state.basicData.password,
				},
				{ root: true },
			);
			// now we have JWT token to update the following
			await dispatch(
				`profile/${ProfileActionKeys.update}`,
				{ language: lang, silent: true },
				{ root: true },
			);
			await dispatch(NgoSignupActionKeys.requestEmailVerification, {
				ngoId: ngo.id,
			});
		},

		async [NgoSignupActionKeys.submitExtra](
			{ state, dispatch },
			{ ngoId }: { ngoId: number },
		) {
			if (!ngoId) throw new Error('Missing ngoId');
			if (!state.extraData) throw new Error('Missing extraData');
			await api.ngoSignupExtra(ngoId, state.extraData as NgoSignupExtraDto);

			// load profile first, otherwise there's a chance that we might get redirected back due to `ngo.onboardingStatus` still being `verified`
			await dispatch(`profile/${ProfileActionKeys.fetch}`, null, {
				root: true,
			});

			// redirect to onboarding page
			router.replace({ path: '/onboarding' });
		},

		[NgoSignupActionKeys.requestEmailVerification](
			_,
			{ ngoId }: { ngoId: number },
		) {
			return api.requestEmailVerification(ngoId);
		},

		[NgoSignupActionKeys.setCurrentStep](
			{ commit },
			{ step }: { step: number },
		) {
			commit(SET_CURRENT_STEP, step);
		},

		async [NgoSignupActionKeys.changeAccountEmail](
			{ dispatch, rootGetters },
			{ email }: { email: string },
		) {
			const ngoId = rootGetters['auth/ngoId'];
			if (!ngoId) throw new Error('Missing NGO Id');
			if (!email) throw new Error('Missing email');
			await api.ngoChangeAccountEmail(ngoId, email);

			// need to get new access token
			await dispatch(`auth/${AuthActionKeys.getNewToken}`, null, {
				root: true,
			});
		},
	},

	getters: {
		[NgoSignupGetterKeys.basicData]: state => state.basicData,
		[NgoSignupGetterKeys.extraData]: state => state.extraData,
		[NgoSignupGetterKeys.currentStep]: state => state.currentStep,
	},
};

export default store;
